import type { CSSProperties } from "react";

type Props = {
  src?: string | null;
  placeholder?: string;
  ratio?: string;
  className?: string;
  style?: CSSProperties;
  fill?: boolean;
  // When false, the empty-state shows only the surface (no caption text). Use
  // for heroes that overlay a title — otherwise the placeholder text collides
  // with the title and both become hard to read.
  caption?: boolean;
  // Set on the one image that's above the fold — a hero — so it isn't
  // deferred along with everything the reader hasn't scrolled to.
  eager?: boolean;
};

export function Photo({
  src,
  placeholder = "No photo yet",
  ratio = "16 / 9",
  className,
  style,
  fill = false,
  caption = true,
  eager = false,
}: Props) {
  const outerStyle: CSSProperties = fill
    ? { position: "absolute", inset: 0, width: "100%", height: "100%", ...style }
    : { aspectRatio: ratio, width: "100%", ...style };
  return (
    <div className={["photo-wrap", className].filter(Boolean).join(" ")} style={outerStyle}>
      {src ? (
        // A real <img>, not a CSS background. Backgrounds can't be lazy-loaded,
        // so a cookbook of thirty recipes used to fetch thirty full-size photos
        // the moment the grid mounted, whether or not you ever scrolled to
        // them. `loading="lazy"` leaves the offscreen ones alone; `decoding`
        // keeps decode work off the main thread. Photos are pasted URLs at
        // whatever size the source serves, so this matters more here than it
        // would with a resizing upload pipeline.
        <img
          className="photo"
          src={src}
          alt=""
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          // The hero is the one image worth racing for; everything else can
          // wait its turn behind the content.
          fetchPriority={eager ? "high" : "low"}
        />
      ) : (
        <div className="photo-slot">{caption ? placeholder : null}</div>
      )}
    </div>
  );
}
